'use client';

import { useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ScheduleSlotButton } from '@/components/ui/schedule-slot-button.component';
import { Button } from '@/components/ui/button/button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { ScheduleType, SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { dateFormatter } from '@/utils/formatters/date';
import { SlotsSkeleton } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_primitives/slots-skeleton.component';
import { formatRelativeDay } from '@/utils/formatters/format-relative-day';
import { useScheduleSlots } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_hooks/use-schedule-slots.hook';
import { SearchCardClinicScheduledModal } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/variants/clinic/_components/tabs/scheduled/search-card-clinic-scheduled-modal.component';

const PREVIEW_LIMIT_DAYS = 1;
const PREVIEW_SLOT_COUNT = 3;

interface ScheduledTabPreviewProps {
  schedule: SearchPartnerData;
}

export function ScheduledTabPreview({ schedule }: ScheduledTabPreviewProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const scheduled = schedule.schedulesTypes[ScheduleType.SCHEDULED];
  const scheduleId = scheduled?.[0]?.id;

  const { isLoading, days } = useScheduleSlots({
    scheduleId,
    limitDays: PREVIEW_LIMIT_DAYS,
  });

  const firstDay = days[0];
  const previewSlots = firstDay?.slots.slice(0, PREVIEW_SLOT_COUNT) ?? [];

  const handleOpenModal = () => setModalOpen(true);

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_TAB_SCHEDULED}
    >
      {isLoading
        ? (
            <SlotsSkeleton variant="card" />
          )
        : (
            <>
              {firstDay && (
                <Typography variant={TypographyVariant.MEDIUM_BODY_TEXT} className="font-semibold">
                  {`Próximos horários: ${formatRelativeDay(firstDay.date)}`}
                </Typography>
              )}
              {previewSlots.length > 0 && (
                <div className="flex flex-wrap gap-2 w-full">
                  {previewSlots.map((slot) => (
                    <ScheduleSlotButton
                      key={slot.id}
                      start={dateFormatter(slot.start, 'HH:mm')}
                      className="flex-1 basis-0 min-w-[80px]"
                    />
                  ))}
                </div>
              )}
              <Button
                variant="default"
                className="w-full h-10 py-0 text-base"
                data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_SCHEDULED_OPEN_MODAL}
                onClick={handleOpenModal}
              >
                Ver mais horários
              </Button>
            </>
          )}

      <SearchCardClinicScheduledModal
        scheduleId={scheduleId}
        schedule={schedule}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </div>
  );
}
